import type { CSSProperties, ReactNode } from "react";
import { doctorIcon, doctorToneKey, mapDoctorLabel } from "../doctor";
import type { DoctorLabel, DoctorRaw } from "../types";

const TONE_STYLE: Record<
  ReturnType<typeof doctorToneKey>,
  { background: string; color: string; ring: string }
> = {
  success: {
    background: "rgba(16, 185, 129, 0.15)",
    color: "#6ee7b7",
    ring: "rgba(16, 185, 129, 0.35)",
  },
  warning: {
    background: "rgba(245, 158, 11, 0.15)",
    color: "#fcd34d",
    ring: "rgba(245, 158, 11, 0.35)",
  },
  danger: {
    background: "rgba(244, 63, 94, 0.15)",
    color: "#fda4af",
    ring: "rgba(244, 63, 94, 0.35)",
  },
  neutral: {
    background: "rgba(100, 116, 139, 0.15)",
    color: "#cbd5e1",
    ring: "rgba(100, 116, 139, 0.35)",
  },
};

export type RuntimeDoctorStatusProps = {
  /** Raw doctor_status or already-mapped DoctorLabel. */
  status: DoctorRaw | DoctorLabel;
  /** Optional prefix (e.g. omit when standing alone). */
  showLabel?: boolean;
  className?: string;
  style?: CSSProperties;
};

/**
 * ONE shared Runtime Doctor status pill.
 * States ONLY: Pass | Warn | Fail | Not scanned (| Unknown).
 */
export function RuntimeDoctorStatus({
  status,
  showLabel = true,
  className = "",
  style,
}: RuntimeDoctorStatusProps): ReactNode {
  const label: DoctorLabel =
    status === "Pass" ||
    status === "Warn" ||
    status === "Fail" ||
    status === "Not scanned" ||
    status === "Unknown"
      ? status
      : mapDoctorLabel(status);

  const tone = doctorToneKey(label);
  const colors = TONE_STYLE[tone];
  const icon = doctorIcon(label);

  return (
    <span
      className={`runtime-doctor-status runtime-doctor-status--${tone} ${className}`.trim()}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.35rem",
        borderRadius: "9999px",
        padding: "0.15rem 0.65rem",
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 1.4,
        background: colors.background,
        color: colors.color,
        boxShadow: `inset 0 0 0 1px ${colors.ring}`,
        ...style,
      }}
      role="status"
      aria-label={`Runtime Doctor: ${label}`}
      data-doctor={label}
    >
      <span aria-hidden="true" style={{ fontWeight: 700 }}>
        {icon}
      </span>
      {showLabel ? <span>{label}</span> : null}
    </span>
  );
}
